import { Skeleton } from "@/components/ui";
import { toast } from "@/components/ui";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useWindowSize } from "react-use";
import { PatientTab } from "./components/PatientTab";
import { PatientsMinimized } from "./components/PatientsMinimized";
import { TAKE_NEXT_PATIENT_TAB_WIDTH } from "./components/TakeNextPatientTab";
import { useNextAssignedPatient } from "./hooks/useNextAssignedPatient";
import { usePreviewTabs } from "./hooks/usePreviewTabs";
import {
  type PatientTabsResponse_Tab,
  usePatientTabs,
} from "./network/usePatientTabs";
import {
  calculateDisplayedAndHiddenTabs,
  findOtherPatientTabs,
  isSelected,
} from "./utils";

interface TabGroup {
  displayed: Tab[];
  hidden: Tab[];
}

export interface Tab extends PatientTabsResponse_Tab {
  hasOtherOpenTabs: boolean;
}

export const PatientTabs = () => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const ref = useRef<HTMLDivElement>(null);
  const window = useWindowSize();
  const { patientId } = useParams({ strict: false });
  const { encounterId } = useSearch({ strict: false });
  const encounterIdStr = encounterId != null ? String(encounterId) : undefined;

  usePreviewTabs();

  const [tabs, setTabs] = useState<TabGroup | null>(null);

  const { data, isLoading, isError } = usePatientTabs(isAuthenticated);
  const { next: nextAssignedPatient } = useNextAssignedPatient();

  // biome-ignore lint: window retriggering useEffect allows recalculating tabs to display based on new size of ref width
  useEffect(() => {
    if (!data?.patientTabs) return;

    const containerWidth = Number(ref.current?.clientWidth);

    const tabsWithFlags = findOtherPatientTabs(data?.patientTabs) as Tab[];

    const calculated = calculateDisplayedAndHiddenTabs<Tab>({
      tabs: tabsWithFlags,
      containerWidth,
      reservedWidth: nextAssignedPatient ? TAKE_NEXT_PATIENT_TAB_WIDTH : 0,
    });

    // If selected tab is hidden, pull it into displayed as the last item
    if (patientId || encounterIdStr) {
      const hiddenIndex = calculated.hidden.findIndex((t) =>
        isSelected(t, patientId, encounterIdStr),
      );
      if (hiddenIndex >= 0) {
        const selected = calculated.hidden.splice(hiddenIndex, 1)[0];
        if (calculated.displayed.length > 0) {
          const demoted = calculated.displayed.pop() as Tab;
          calculated.hidden.unshift(demoted);
        }
        calculated.displayed.push(selected);
      }
    }

    setTabs(calculated);
  }, [
    window,
    data?.patientTabs,
    patientId,
    encounterIdStr,
    nextAssignedPatient,
  ]);

  useEffect(() => {
    if (isError) {
      toast.error("Failed to fetch patient tabs", {
        id: "patient-tabs-error",
      });
    }
  }, [isError]);

  if (!isAuthenticated) {
    return null;
  }

  if (!tabs || isLoading) {
    return (
      <div
        data-testid="patient-tabs-skeleton"
        className="flex justify-between w-full"
        ref={ref}
      >
        <Skeleton className="h-8 w-full items-center rounded-none" />
      </div>
    );
  }

  const lastTab = tabs.displayed[tabs.displayed.length - 1];
  const areTabsHidden = tabs?.hidden.length > 0;

  return (
    <div
      data-testid="patient-tabs"
      className="bg-secondary flex items-center gap-px w-full"
      ref={ref}
    >
      {/* <TakeNextPatientTab /> */}

      {tabs?.displayed?.map((tab) => (
        <PatientTab
          key={`${tab.patient.id} + ${tab.encounter?.id}`}
          tab={tab}
          className={tab === lastTab && areTabsHidden ? "pr-8" : ""}
        />
      ))}

      {areTabsHidden && <PatientsMinimized options={tabs?.hidden} />}
    </div>
  );
};
